/**
 * Wires up automatic PWA update delivery. vite-plugin-pwa's default
 * auto-injected registerSW.js only ever calls
 * navigator.serviceWorker.register() once on load — it never checks for a
 * new service worker again after that, relying on the browser's own
 * (infrequent, navigation-tied) update checks. An installed/standalone PWA
 * is almost never fully closed (backgrounding it isn't closing it), so
 * without an explicit check the only way users actually received an update
 * was uninstalling and reinstalling the app.
 *
 * sw.ts already calls self.skipWaiting()/clientsClaim() unconditionally, so
 * a new worker takes over as soon as it's fetched — that part is silent and
 * harmless (it only changes which worker serves the *next* network
 * request). What must NOT be silent is the page reload needed to load the
 * matching JS bundle: registerType is 'prompt' (not 'autoUpdate') so
 * vite-plugin-pwa never reloads on its own — onNeedRefresh below just
 * notifies UpdateAvailableBanner, which lets the user pick when to reload
 * instead of yanking them off whatever they were doing.
 */
import { registerSW } from 'virtual:pwa-register'

const CHECK_INTERVAL_MS = 30 * 60 * 1000 // 30 min

export const SW_UPDATE_AVAILABLE_EVENT = 'olimhub:sw-update-available'

export interface SwUpdateAvailableDetail {
  reload: () => Promise<void>
}

export function initServiceWorkerUpdates(): void {
  if (!('serviceWorker' in navigator)) return

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      const detail: SwUpdateAvailableDetail = { reload: () => updateSW(true) }
      window.dispatchEvent(new CustomEvent(SW_UPDATE_AVAILABLE_EVENT, { detail }))
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return
      // Swallow rejections — most commonly InvalidStateError from a mobile
      // PWA resuming right as the browser is still re-establishing the
      // registration's binding after backgrounding it. Transient and
      // harmless: the next interval/foreground check succeeds once it
      // stabilizes. Without .catch() this becomes an unhandled rejection
      // that Sentry reports as an error on every occurrence.
      const checkForUpdate = () => { registration.update().catch(() => undefined) }
      setInterval(checkForUpdate, CHECK_INTERVAL_MS)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate()
      })
    },
  })
}
